<template>
  <Modal
    :scrollable-wrapper="true"
    :show="show"
    :size="APP_MODAL_SIZES.XL_NEXT"
    :title="$t('dashboard.add.elements_to_dashboard', { dashboardName: modelValue?.name || '' })"
    class="aom-Modal"
    data-auto-testid="add-objective-to-custom-dashboard-modal"
    data-testid="add-objective-to-custom-dashboard-modal"
    manual-close
    sticky-header
    @close="onClose"
  >
    <template #modal-body>
      <BulkAddElementsTable
        :already-added-elements="hiddenItems"
        :limit="MAX_OBJECTIVES"
        :loading="isLoading"
        @on-confirm="onConfirm"
      />
    </template>
  </Modal>
</template>

<script setup>
import { isEmpty } from 'lodash'
import { computed, ref } from 'vue'

import CustomDashboardApiHandler from '@/api/custom-dashboard'
import { tracker } from '@/tracking/amplitude'
import { EVENT_NAMES } from '@/tracking/gtm-helpers'
import { gtmTracker } from '@/tracking/gtm-tracking'
import { APP_MODAL_SIZES } from '@/utils/components-configurations/app-modal'
import {
  CUSTOM_DASHBOARD_MODULE_TYPES_FOR_TRACKING,
  MAX_OBJECTIVES,
  MODULE_TYPE_IDS
} from '@/utils/custom-dashboard-helper'
import { handleError } from '@/utils/error-handling'
import { isKPI } from '@/utils/objectives'

import BulkAddElementsTable from '@/components/objectives/BulkAddElementsTable'
import Modal from '@/components/ui/Modal/Modal'

defineOptions({
  name: 'AddObjectiveModal'
})

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  },

  modelValue: {
    type: Object,
    default: () => ({})
  },

  dashboardItem: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits({
  'on-close': null,
  'on-success': null
})

const onClose = () => {
  emit('on-close')
}

const isLoading = ref(false)
const onConfirm = async ({ elementsToAdd = [], elementsData = {} } = {}) => {
  if (isEmpty(elementsToAdd)) {
    onClose()
    return
  }

  const customDashboardApi = new CustomDashboardApiHandler()
  try {
    isLoading.value = true
    await customDashboardApi.addElementsToModule({
      dashboardId: props.dashboardItem.id,
      moduleId: props.modelValue.id,
      elementIds: elementsToAdd
    })
    emit('on-success', props.modelValue)

    tracker.logEvent('element added', {
      total_elements: elementsToAdd.length + props.modelValue.elements.length,
      module_type: CUSTOM_DASHBOARD_MODULE_TYPES_FOR_TRACKING[props.modelValue.typeId]
    })

    if (props.modelValue.typeId === MODULE_TYPE_IDS.KPI) {
      const isKPIAdded = Object.values(elementsData).some(el => isKPI(el))

      if (isKPIAdded) {
        gtmTracker.logEvent(EVENT_NAMES.KPI_ADDED_TO_DASHBOARD)
      }
    }

    onClose()
  } catch (error) {
    handleError({ error })
  } finally {
    isLoading.value = false
  }
}
const hiddenItems = computed(() => props.modelValue.elements.map(i => i.id))
</script>

<style lang="scss" scoped>
.aom-Modal {
  --modal-header-padding: 40px var(--page-right-padding) 12px var(--page-left-padding);
  --modal-title-margin: 0;
  --modal-title-line-height: 28px;

  &:deep(.o-modal-body) {
    padding: 0;
  }

  :deep(.o-modal-content) {
    height: auto;
  }
}
</style>
